/**
 * Centralized navigation configuration
 * This contains all navigation items from Layout.jsx in one place
 * Single source of truth for navigation across the application
 */

import {
  Heart,
  BarChart3,
  FileText,
  Stethoscope,
  CreditCard,
  Settings,
  Activity,
  TrendingUp,
  Users,
  Package,
  Pill,
  Calendar,
  TestTube,
  Clock,
} from "lucide-react";

// Main navigation items
export const navigationItems = [
  {
    title: "Dashboard",
    navigationItem: "Dashboard",
    route: "/dashboard",
    icon: Activity,
    color: "text-blue-600",
  },
  {
    title: "Calendar",
    navigationItem: "Patient Calendar",
    route: "/patientportal",
    icon: Calendar,
    color: "text-green-600",
    userType: "patient",
  },
  {
    title: "Patient Management",
    navigationItem: "Patient Management",
    route: "/patientmanagement",
    icon: Users,
    color: "text-blue-600",
  },
  {
    title: "Blood Tests",
    navigationItem: "Blood Tests",
    route: "/bloodtests",
    icon: Heart,
    color: "text-red-500",
  },
  {
    title: "Biological Age",
    navigationItem: "Biological Age",
    route: "/biologicalage",
    icon: Clock,
    color: "text-purple-600",
  },
  {
    title: "Concierge Testing",
    navigationItem: "Concierge Testing",
    route: "/conciergetesting",
    icon: TestTube,
    color: "text-sky-600",
  },
  {
    title: "Analytics",
    navigationItem: "Analytics",
    route: "/analytics",
    icon: TrendingUp,
    color: "text-green-600",
  },
  {
    title: "Reports",
    navigationItem: "Reports",
    route: "/reports",
    icon: FileText,
    color: "text-purple-600",
  },
  {
    title: "Clinical Notes",
    navigationItem: "Clinical Notes",
    route: "/clinicalnotes",
    icon: Stethoscope,
    color: "text-indigo-600",
  },
  {
    title: "Services",
    navigationItem: "Services",
    route: "/services",
    icon: Stethoscope,
    color: "text-emerald-600",
  },
  {
    title: "Prescriptions",
    navigationItem: "Prescriptions",
    route: "/prescriptions",
    icon: Pill,
    color: "text-rose-600",
  },
  {
    title: "Calendar",
    navigationItem: "Calendar",
    route: "/calendar",
    icon: Calendar,
    color: "text-rose-600",
    userType: "doctor",
  },
  {
    title: "Inventory",
    navigationItem: "Inventory",
    route: "/inventory",
    icon: Package,
    color: "text-teal-600",
  },
  {
    title: "Billing",
    navigationItem: "Billing",
    route: "/comprehensivebilling",
    icon: CreditCard,
    color: "text-orange-600",
  },
  {
    title: "Wearables",
    navigationItem: "Wearables",
    route: "/wearables",
    icon: BarChart3,
    color: "text-cyan-600",
  },
];

// Footer navigation items
export const footerNavigationItems = [
  {
    title: "Settings",
    navigationItem: "Settings",
    route: "/settings",
    icon: Settings,
    color: "text-slate-600",
  },
];

/**
 * Check if a user can access a specific route based on their navigation permissions
 * This uses the same logic as the Layout.jsx navigation filtering
 */
export const canUserAccessRoute = (
  routePath,
  user,
  navigationPermissions,
  hasAnyPermission
) => {
  if (!user || !navigationPermissions || !hasAnyPermission) {
    return false;
  }

  // For all other routes, check against navigation permissions
  const navigationItem = [...navigationItems, ...footerNavigationItems].find(
    (item) => item.route === routePath
  );
  if (!navigationItem) {
    // Route not in our navigation system, allow access (might be dynamic routes like /patients/:id)
    return true;
  }

  const requiredPermissions =
    navigationPermissions[navigationItem.navigationItem];
  return requiredPermissions && hasAnyPermission(requiredPermissions);
};

/**
 * Get the appropriate redirect route for a user type
 */
export const getDefaultRouteForUserType = (userType) => {
  switch (userType) {
    case "patient":
      return "/dashboard"; // Both go to /dashboard now but render different components
    case "doctor":
    case "practiceOwner":
      return "/dashboard";
    default:
      return "/dashboard";
  }
};

/**
 * Get route for a navigation title
 */
export const getRouteForNavigation = (navigationTitle) => {
  const item = [...navigationItems, ...footerNavigationItems].find(
    (item) => item.navigationItem === navigationTitle
  );
  return item ? item.route : null;
};
